import { CardElement } from '@stripe/react-stripe-js';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import SubscriptionStore from '../../../redux/subscriptions';
import { Icon, icons } from '../../generic/Icon';
import type { AsyncStatus, FossaSubscriptionPlan } from '../../../types';
import Loader from '../../generic/Loader';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import useOnMount from '../../../hooks/useOnMount';
import Flex from '../../generic/Flex';
import FormLabel from '../../generic/Forms/FormLabel';
import styles from './CreditCardComponent.module.less';

type ReduxProps = {
  fetchSubscription(): void;
  subscription: FossaSubscriptionPlan;
  status: AsyncStatus;
};

type Props = {
  onChange?: (event: StripeCardElementChangeEvent) => any;
  onEdit: (isEditing: boolean) => void;
  isEditing?: boolean;
  paymentError?: string;
  isDisabled?: boolean;
} & ReduxProps;

const cardWrapperStyles = {
  border: 'solid 1px #ccc',
  borderRadius: '3px',
  padding: '15px',
  background: '#fff',
  width: '24em',
};

/**
 * Simple credit card component that provides the ability to add or update a credit card through stripe.
 * This module provides feedback to whether or not there is a card stored
 *
 * NOTES:
 * this component does not handle its own state - isEditing must be toggled by the parent
 * this component does not update payment tokens. that must be handled by the parent component.
 *
 * onChange() - triggers when a credit card has finished being entered
 * onEdit() - triggers when the form has changed to editable
 * initialEditState - The initial form state - may be overrode if no payment method is found
 */
const CreditCardComponent = ({
  fetchSubscription,
  subscription,
  status,
  isEditing = false,
  paymentError,
  onChange,
  onEdit,
  isDisabled = false,
}: Props) => {
  useOnMount(() => {
    if (!subscription) {
      fetchSubscription();
    }
  });

  if (status.loading || !subscription) {
    return <Loader />;
  }

  return (
    <div className="creditcard-component">
      <FormLabel label="Payment Method" htmlFor="card-element" isInline>
        {paymentError && <small className={styles.smallError}>{paymentError}</small>}
        {(subscription.hasPaymentMethod ? isEditing : true) ? (
          <div style={cardWrapperStyles}>
            <CardElement
              id="card-element"
              onChange={onChange}
              options={{
                disabled: isDisabled,
              }}
            />
          </div>
        ) : (
          <Flex justify="left" gap="1em">
            <div className="font-weight-normal">
              <Flex justify="left" gap="0.5em">
                <Icon icon={icons.creditCard} />
                <span>●●●●</span>
                <span>●●●●</span>
                <span>●●●●</span>
                <span>{subscription.paymentInfo.last4}</span>
              </Flex>
            </div>
            {' '}
            <Button
              type={BUTTON_TYPE.ANCHOR}
              size={BUTTON_SIZE.MICRO}
              onClick={() => onEdit(!isEditing)}
            >
              Update Card
            </Button>
          </Flex>
        )}
      </FormLabel>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  status: SubscriptionStore.selectors.getLoading,
  subscription: SubscriptionStore.selectors.getSubscription,
});

const mapDispatchToProps = {
  fetchSubscription: SubscriptionStore.actions.fetchSubscription,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditCardComponent);
